import React from "react";
import { Slot } from "@radix-ui/react-slot";
import cx from "classnames";

import { BooleanPropDef, ComponentPropsWithout, EnumPropDef, GetPropDefTypes } from "types/types";

const noop = () => {};

const accentColors = ['none', 'gray', 'red', 'blue', 'green'] as const;
const borderStyles = ['none', 'full', 'top', 'bottom'] as const;
const radii = ['none', 'small', 'medium', 'large', 'full'] as const;
const appearances = ['inherit', 'light', 'dark'] as const;
const scalings = ['90%', '95%', '100%', '105%', '110%'] as const;

const ThemeProps: {
  asChild: BooleanPropDef;
  borderStyle: EnumPropDef<string>;
  appearance: EnumPropDef<string>;
  accentColor: EnumPropDef<string>;
  radius: EnumPropDef<string>;
  scaling: EnumPropDef<string>;
} = {
  asChild: { type: 'boolean' },
  borderStyle: { type: 'enum', values: borderStyles, default: 'none' },
  appearance: { type: 'enum', values: appearances, default: 'inherit' },
  accentColor: { type: 'enum', values: accentColors, default: 'none' },
  radius: { type: 'enum', values: radii, default: 'medium' },
  scaling: { type: 'enum', values: scalings, default: '100%' },
};

type ThemeBorderStyle = (typeof ThemeProps.borderStyle.values)[number];
type ThemeAppearance = (typeof ThemeProps.appearance.values)[number];
type ThemeAccentColor = (typeof ThemeProps.accentColor.values)[number];
type ThemeRadius = (typeof ThemeProps.radius.values)[number];
type ThemeScaling = (typeof ThemeProps.scaling.values)[number];

interface ThemeChangeHandlers {
  onBorderStyleChange: (borderStyle: ThemeBorderStyle) => void;
  onAppearanceChange: (appearance: ThemeAppearance) => void;
  onAccentColorChange: (accentColor: ThemeAccentColor) => void;
  onRadiusChange: (radius: ThemeRadius) => void;
  onScalingChange: (scaling: ThemeScaling) => void;
}

interface ThemeContextValue extends ThemeChangeHandlers {
  borderStyle: ThemeBorderStyle;
  appearance: ThemeAppearance;
  accentColor: ThemeAccentColor;
  radius: ThemeRadius;
  scaling: ThemeScaling;
}

type ThemeImplPrivatePropDefs = Partial<ThemeChangeHandlers> & {
  isRoot?: boolean;
}

interface ThemePropDefs extends ComponentPropsWithout<'div', 'dir'>, GetPropDefTypes<typeof ThemeProps> {}

type ThemeImplPropDefs = ThemePropDefs & ThemeImplPrivatePropDefs;

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

function useThemeContext() {
  const context = React.useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('`useThemeContext` must be used within a `Theme`');
  }

  return context;
}

const ThemeImpl = React.forwardRef<React.ElementRef<'div'>, ThemeImplPropDefs>((props, forwardedRef) => {
  const context = React.useContext(ThemeContext);

  const {
    asChild,
    isRoot,
    //
    borderStyle = context?.borderStyle ?? ThemeProps.borderStyle.default,
    appearance = context?.appearance ?? ThemeProps.appearance.default,
    accentColor = context?.accentColor ?? ThemeProps.accentColor.default,
    radius = context?.radius ?? ThemeProps.radius.default,
    scaling = context?.scaling ?? ThemeProps.scaling.default,
    //
    onBorderStyleChange = noop,
    onAppearanceChange = noop,
    onAccentColorChange = noop,
    onRadiusChange = noop,
    onScalingChange = noop,
    //
    ...themeProps
  } = props;

  const Comp = asChild ? Slot : 'div';
  // const hasBackground = isRoot || appearance === 'light' || appearance === 'dark';

  return (
    <ThemeContext.Provider
      value={React.useMemo(
        () => ({
          borderStyle,
          appearance,
          accentColor,
          radius,
          scaling,
          onBorderStyleChange,
          onAppearanceChange,
          onAccentColorChange,
          onRadiusChange,
          onScalingChange,
        }),
        [
          borderStyle,
          appearance,
          accentColor,
          radius,
          scaling,
          onBorderStyleChange,
          onAppearanceChange,
          onAccentColorChange,
          onRadiusChange,
          onScalingChange,
        ]
      )}
    >
    <Comp
      data-is-root-theme={isRoot ? 'true' : 'false'}
      data-accent-color={accentColor}
      data-radius={radius}
      data-scaling={scaling}
      data-border-style={borderStyle}
      ref={forwardedRef}
      {...themeProps}
      className={cx(
        'intercom-themes',
        {
          light: appearance === 'light',
          dark: appearance === 'dark',
        },
        themeProps.className
      )}
    />
    </ThemeContext.Provider>
  )
});

ThemeImpl.displayName = 'ThemeImpl';

const ThemeRoot = React.forwardRef<React.ElementRef<'div'>, ThemePropDefs>((props, forwardedRef) => {
  const {
    borderStyle: borderProp = ThemeProps.borderStyle.default,
    appearance: appearanceProp = ThemeProps.appearance.default,
    accentColor: accentColorProp = ThemeProps.accentColor.default,
    radius: radiusProp = ThemeProps.radius.default,
    scaling: scalingProp = ThemeProps.scaling.default,
    ...ComponentProps
  } = props;

  const [borderStyle, setBorderStyle] = React.useState(borderProp);

  React.useEffect(() => setBorderStyle(borderStyle), [borderStyle])

  const [appearance, setAppearance] = React.useState(appearanceProp);

  React.useEffect(() => setAppearance(appearanceProp), [appearanceProp]);

  const [accentColor, setAccentColor] = React.useState(accentColorProp);

  React.useEffect(() => setAccentColor(accentColorProp), [accentColorProp]);

  const [radius, setRadius] = React.useState(radiusProp);

  React.useEffect(() => setRadius(radiusProp), [radiusProp]);

  const [scaling, setScaling] = React.useState(scalingProp);

  React.useEffect(() => setScaling(scalingProp), [scalingProp]);

  return (
    <ThemeImpl
      {...ComponentProps}
      ref={forwardedRef}
      isRoot
      //
      borderStyle={borderStyle}
      appearance={appearance}
      accentColor={accentColor}
      radius={radius}
      scaling={scaling}
      //
      onBorderStyleChange={setBorderStyle}
      onAppearanceChange={setAppearance}
      onAccentColorChange={setAccentColor}
      onRadiusChange={setRadius}
      onScalingChange={setScaling}
    />
  );
});

ThemeRoot.displayName = 'ThemeRoot';

const Theme = React.forwardRef<React.ElementRef<'div'>, ThemePropDefs>((props, forwardedRef) => {
  const context = React.useContext(ThemeContext);
  const isRoot = context === undefined;

  return isRoot ? <ThemeRoot {...props} ref={forwardedRef} /> : <ThemeImpl {...props} ref={forwardedRef} />;
});

Theme.displayName = 'Theme';

export {
  Theme,
  useThemeContext
};
